/**
 * Centralized Firestore Collection Path Utilities
 *
 * This file provides utilities for constructing Firestore collection paths
 * for all feature modules. All features MUST use these functions instead of
 * constructing paths manually.
 *
 * PATH STRUCTURE:
 * All collections use the nested structure: .../collection/{locationId}/items/{documentId}
 * This enables wildcard-based Firestore rules for unlimited location scalability.
 *
 * @module shared/config/collections
 */

import { appId, isProduction } from './firebase';

/**
 * Collection types supported by the application
 */
type CollectionType =
  | 'rental'
  | 'stamps'
  | 'vendors'
  | 'backorders'
  | 'departmentSales'
  | 'directory'
  | 'trucking'
  | 'manualCalendarEntries'
  | 'archivedCalendarEntries'
  | 'truckingSchedule'
  | 'roles'
  | 'divisions'
  | 'references'
  | 'bolHistory'
  | 'bolAddresses'
  | 'bolCarriers'
  | 'lostRevenue'
  | 'guides'
  | 'userNotifications';

/**
 * Environment type for collection paths
 */
type EnvironmentType = 'prod' | 'dev';

/**
 * Get environment string for collection paths
 *
 * @param _collectionType - Type of collection (unused, kept for API consistency)
 * @param useProduction - Whether to use production path (defaults to isProduction)
 * @returns Environment string for path construction
 */
function getEnvironment(
  _collectionType: CollectionType,
  useProduction: boolean = isProduction
): EnvironmentType {
  return useProduction ? 'prod' : 'dev';
}


/**
 * Get the collection path for one user's personal (single-recipient)
 * notifications: `artifacts/{appId}/{env}/shared/userNotifications/{email}/items`.
 *
 * Unlike the root broadcast `notifications` collection this is env-namespaced,
 * so test-site notifications never appear on prod. The email is lowercased so it
 * always matches the recipient's Firebase uid (the security rules key on it).
 *
 * @param email - Recipient email (any case)
 * @param useProduction - Whether to use the production path (defaults to isProduction)
 */
export function getUserNotificationsCollectionPath(
  email: string,
  useProduction: boolean = isProduction
): string {
  const environment = getEnvironment('userNotifications', useProduction);
  return `artifacts/${appId}/${environment}/shared/userNotifications/${email.trim().toLowerCase()}/items`;
}

/**
 * Get collection path for rental equipment tracking
 *
 * @param locationId - Store location ID (11, 12, 13, etc.)
 * @param useProduction - Whether to use production collection (defaults to isProduction)
 * @returns Full Firestore collection path
 *
 * @example
 * getRentalCollectionPath('11') // => 'artifacts/stamp-board-8b2fa/prod/rental/rental-board/11/items'
 */
export function getRentalCollectionPath(
  locationId: string,
  useProduction: boolean = isProduction
): string {
  const environment = getEnvironment('rental', useProduction);
  return `artifacts/${appId}/${environment}/rental/rental-board/${locationId}/items`;
}

/**
 * Get collection path for stamps board
 *
 * @param locationId - Store location ID (11, 12, 13, etc.)
 * @param useProduction - Whether to use production collection (defaults to isProduction)
 * @returns Full Firestore collection path
 *
 * @example
 * getStampsCollectionPath('11') // => 'artifacts/stamp-board-8b2fa/prod/parts/stamp-board/11/items'
 */
export function getStampsCollectionPath(
  locationId: string,
  useProduction: boolean = isProduction
): string {
  const environment = getEnvironment('stamps', useProduction);
  return `artifacts/${appId}/${environment}/parts/stamp-board/${locationId}/items`;
}

/**
 * Get collection path for vendor login credentials
 *
 * @param locationId - Store location ID (11, 12, 13, etc.)
 * @param useProduction - Whether to use production collection (defaults to isProduction)
 * @returns Full Firestore collection path
 *
 * @example
 * getVendorsCollectionPath('11') // => 'artifacts/stamp-board-8b2fa/prod/parts/vendors/11/items'
 */
export function getVendorsCollectionPath(
  locationId: string,
  useProduction: boolean = isProduction
): string {
  const environment = getEnvironment('vendors', useProduction);
  return `artifacts/${appId}/${environment}/parts/vendors/${locationId}/items`;
}

/**
 * Get collection path for parts backorder lines (docs/BACKORDERS.md).
 * Each line doc has an append-only `activity` subcollection.
 */
export function getBackordersCollectionPath(
  locationId: string,
  useProduction: boolean = isProduction
): string {
  const environment = getEnvironment('backorders', useProduction);
  return `artifacts/${appId}/${environment}/parts/backorders/${locationId}/items`;
}

/** Activity log for one backorder line: `.../backorders/{loc}/items/{lineId}/activity` */
export function getBackorderActivityCollectionPath(
  locationId: string,
  lineId: string,
  useProduction: boolean = isProduction
): string {
  return `${getBackordersCollectionPath(locationId, useProduction)}/${lineId}/activity`;
}

/** Backorder import runs (written by the backend): `.../parts/backorder-imports/{loc}/items` */
export function getBackorderImportsCollectionPath(
  locationId: string,
  useProduction: boolean = isProduction
): string {
  const environment = getEnvironment('backorders', useProduction);
  return `artifacts/${appId}/${environment}/parts/backorder-imports/${locationId}/items`;
}

/** Global backorder flag thresholds doc (shared/config rule: read signed-in, write admin) */
export function getBackorderThresholdsDocPath(useProduction: boolean = isProduction): string {
  const environment = getEnvironment('backorders', useProduction);
  return `artifacts/${appId}/${environment}/shared/config/backorders`;
}

/**
 * Get collection path for employee directory
 * Note: Directory is shared across all locations (no location ID)
 *
 * When divisionId is provided, returns a division-scoped subcollection path.
 * When divisionId is omitted/undefined, returns the legacy global path for backward compatibility.
 *
 * @param divisionId - Optional division ID for division-scoped data
 * @param useProduction - Whether to use production collection (defaults to isProduction)
 * @returns Full Firestore collection path, or null if divisionId is null
 *
 * @example
 * getDirectoryCollectionPath() // => 'artifacts/stamp-board-8b2fa/prod/shared/directory'
 * getDirectoryCollectionPath('default') // => 'artifacts/stamp-board-8b2fa/prod/shared/directory/default/items'
 */
export function getDirectoryCollectionPath(
  divisionId?: string | null,
  useProduction: boolean = isProduction
): string | null {
  const environment = getEnvironment('directory', useProduction);
  if (divisionId) {
    return `artifacts/${appId}/${environment}/shared/directory/${divisionId}/items`;
  }
  // Legacy global path (backward compatibility)
  return `artifacts/${appId}/${environment}/shared/directory`;
}


/**
 * Get collection path for trucking companies
 *
 * @param locationId - Store location ID (11, 12, 13, etc.)
 * @param useProduction - Whether to use production collection (defaults to isProduction)
 * @returns Full Firestore collection path
 *
 * @example
 * getTruckingCollectionPath('11') // => 'artifacts/stamp-board-8b2fa/prod/rental/trucking-companies/11/items'
 */
export function getTruckingCollectionPath(
  locationId: string,
  useProduction: boolean = isProduction
): string {
  const environment = getEnvironment('trucking', useProduction);
  return `artifacts/${appId}/${environment}/rental/trucking-companies/${locationId}/items`;
}

/**
 * Get collection path for manual trucking calendar entries
 *
 * These entries are separate from rental-derived deliveries and allow
 * users to schedule trucking for sales, service, and other non-rental needs.
 *
 * @param locationId - Store location ID (11, 12, 13, etc.)
 * @param useProduction - Whether to use production collection (defaults to isProduction)
 * @returns Full Firestore collection path
 *
 * @example
 * getManualCalendarEntriesPath('11') // => 'artifacts/stamp-board-8b2fa/prod/rental/manual-calendar-entries/11/calendar-entries'
 */
export function getManualCalendarEntriesPath(
  locationId: string,
  useProduction: boolean = isProduction
): string {
  const environment = getEnvironment('manualCalendarEntries', useProduction);
  return `artifacts/${appId}/${environment}/rental/manual-calendar-entries/${locationId}/calendar-entries`;
}

/**
 * Get collection path for archived trucking calendar entries
 *
 * These entries are created automatically when a rental with trucking data
 * is cleared. They preserve historical delivery/pickup information.
 *
 * @param locationId - Store location ID (11, 12, 13, etc.)
 * @param useProduction - Whether to use production collection (defaults to isProduction)
 * @returns Full Firestore collection path
 *
 * @example
 * getArchivedCalendarEntriesPath('11') // => 'artifacts/stamp-board-8b2fa/prod/rental/archived-calendar-entries/11/archived-trucking-events'
 */
export function getArchivedCalendarEntriesPath(
  locationId: string,
  useProduction: boolean = isProduction
): string {
  const environment = getEnvironment('archivedCalendarEntries', useProduction);
  return `artifacts/${appId}/${environment}/rental/archived-calendar-entries/${locationId}/archived-trucking-events`;
}

/**
 * Get collection path for trucking schedule index entries
 *
 * Lightweight index documents written when trucking data is set on rental items.
 * Queried by the backend cron to find upcoming trucking events without downloading
 * entire RTDB rental trees.
 *
 * @param locationId - Store location ID (11, 12, 13, etc.)
 * @param useProduction - Whether to use production collection (defaults to isProduction)
 * @returns Full Firestore collection path
 *
 * @example
 * getTruckingSchedulePath('11') // => 'artifacts/stamp-board-8b2fa/prod/rental/trucking-schedule/11/schedule-entries'
 */
export function getTruckingSchedulePath(
  locationId: string,
  useProduction: boolean = isProduction
): string {
  const environment = getEnvironment('truckingSchedule', useProduction);
  return `artifacts/${appId}/${environment}/rental/trucking-schedule/${locationId}/schedule-entries`;
}

/**
 * Get collection path for user roles
 *
 * Roles define bundles of page permissions that can be assigned to users.
 * This is a global collection (not location-specific).
 *
 * @returns Full Firestore collection path
 *
 * @example
 * getRolesCollectionPath() // => 'roles'
 */
export function getRolesCollectionPath(): string {
  // Roles are stored at the root level, not under artifacts
  // This keeps them accessible across all environments
  return 'roles';
}

/**
 * Get collection path for divisions
 *
 * Divisions are stored at the root level (like roles), not under artifacts.
 *
 * @returns Full Firestore collection path
 *
 * @example
 * getDivisionsCollectionPath() // => 'divisions'
 */
export function getDivisionsCollectionPath(): string {
  return 'divisions';
}

/**
 * Get document path for rental category configuration
 *
 * Categories are stored per-location to allow different equipment types at each location.
 *
 * @param locationId - Store location ID (11, 12, 13, etc.)
 * @returns Full Firestore document path
 *
 * @example
 * getRentalCategoriesPath('11') // => 'config/rentalConfig/categoriesByLocation/11'
 */
export function getRentalCategoriesPath(locationId: string): string {
  return `config/rentalConfig/categoriesByLocation/${locationId}`;
}

// =============================================================================
// REALTIME DATABASE PATHS
// =============================================================================
// These paths are for data migrated to Firebase Realtime Database for cost optimization.
// RTDB is significantly cheaper for large collections with frequent reads (like rental/stamps boards)
// because it charges for bandwidth rather than per-document reads.

// =============================================================================
// RTDB EMAIL ENCODING HELPERS
// =============================================================================
// RTDB keys cannot contain: . $ # [ ] /
// We encode emails by replacing '.' with ',' for RTDB key compatibility.
// Other characters like + are allowed in RTDB keys.

/**
 * Encode email for use as RTDB key
 *
 * RTDB keys cannot contain periods (.), so we replace them with commas.
 * This is used for the permissions lookup path and the JWT `ek` claim.
 *
 * @param email - The email address to encode
 * @returns Encoded email safe for RTDB keys
 *
 * @example
 * encodeEmailForRTDB('user@example.com') // => 'user@example,com'
 * encodeEmailForRTDB('first.last@domain.co.uk') // => 'first,last@domain,co,uk'
 */
export function encodeEmailForRTDB(email: string): string {
  return email.toLowerCase().replace(/\./g, ',');
}


/**
 * Get RTDB path for user permissions
 *
 * Permissions are stored per-user in RTDB to enable security rules
 * to look up permissions without requiring them in JWT claims.
 *
 * Note: Unlike rental/stamps data paths, permissions are NOT environment-specific.
 * This matches Firestore's /users/{email} which is also shared across environments.
 *
 * @param email - User's email address
 * @returns RTDB path to user's permissions
 *
 * @example
 * getPermissionsRTDBPath('user@example.com') // => 'permissions/user@example,com'
 */
export function getPermissionsRTDBPath(email: string): string {
  return `permissions/${encodeEmailForRTDB(email)}`;
}

/**
 * Get RTDB path for rental equipment tracking
 *
 * @param locationId - Store location ID (11, 12, 13, etc.)
 * @param useProduction - Whether to use production path (defaults to isProduction)
 * @returns RTDB path
 *
 * @example
 * getRentalRTDBPath('11') // => 'rentals/prod/11'
 */
export function getRentalRTDBPath(
  locationId: string,
  useProduction: boolean = isProduction
): string {
  const environment = useProduction ? 'prod' : 'dev';
  return `rentals/${environment}/${locationId}`;
}

/**
 * Get RTDB path for stamps board
 *
 * @param locationId - Store location ID (11, 12, 13, etc.)
 * @param useProduction - Whether to use production path (defaults to isProduction)
 * @returns RTDB path
 *
 * @example
 * getStampsRTDBPath('11') // => 'stamps/prod/11'
 */
export function getStampsRTDBPath(
  locationId: string,
  useProduction: boolean = isProduction
): string {
  const environment = useProduction ? 'prod' : 'dev';
  return `stamps/${environment}/${locationId}`;
}

/**
 * Get RTDB path for rental tag colors configuration
 *
 * Tag colors are stored at the location level so all items with the same
 * tag name display the same color across the board.
 *
 * @param locationId - Store location ID (11, 12, 13, etc.)
 * @param useProduction - Whether to use production path (defaults to isProduction)
 * @returns RTDB path
 *
 * @example
 * getRentalTagColorsPath('11') // => 'rentals/prod/11/_config/tagColors'
 */
export function getRentalTagColorsPath(
  locationId: string,
  useProduction: boolean = isProduction
): string {
  const environment = useProduction ? 'prod' : 'dev';
  return `rentals/${environment}/${locationId}/_config/tagColors`;
}

/**
 * Get RTDB path for a reservation's extracted audit log node.
 *
 * Audit logs live OUTSIDE the live board node (they are ~34% of board bytes and
 * only relevant for the lifetime of one reservation). They are stored as
 * push-keyed children so appends are one tiny write instead of an array rewrite:
 *
 *   auditLogs/{board}/{env}/{locationId}/{itemId}/{reservationId}/{pushKey} = AuditEntry
 *
 * When `reservationId` is omitted, returns the item-level parent node (used to
 * delete every reservation's audit history when an item itself is deleted).
 *
 * @param board - 'rentals' | 'stamps' (matches the live board top-level node)
 * @param locationId - Store location ID (11, 12, 13, etc.)
 * @param itemId - Board item ID
 * @param reservationId - Reservation identifier stored in `set.details.reservationId`
 * @param useProduction - Whether to use production path (defaults to isProduction)
 * @returns RTDB path
 *
 * @example
 * getAuditLogRTDBPath('rentals', '11', 'item-1', 'r123-ab12') // => 'auditLogs/rentals/prod/11/item-1/r123-ab12'
 * getAuditLogRTDBPath('rentals', '11', 'item-1') // => 'auditLogs/rentals/prod/11/item-1'
 */
export function getAuditLogRTDBPath(
  board: 'rentals' | 'stamps',
  locationId: string,
  itemId: string,
  reservationId?: string,
  useProduction: boolean = isProduction
): string {
  const environment = useProduction ? 'prod' : 'dev';
  return getAuditLogRTDBPathFromBoardPath(`${board}/${environment}/${locationId}`, itemId, reservationId);
}

/**
 * Build the audit log node path from an already-composed board path
 * (e.g. `config.getRTDBPath(locationId)` which returns `rentals/prod/11`).
 *
 * This is the single implementation of the audit path shape; the
 * board/location/production variant above delegates here. Use this variant when
 * you already hold the composed board path (most hooks do) so board/env/location
 * never has to be re-derived.
 *
 * @param boardPath - Composed board path, e.g. 'rentals/prod/11'
 * @param itemId - Board item ID
 * @param reservationId - Optional reservation identifier
 * @returns RTDB path
 */
export function getAuditLogRTDBPathFromBoardPath(
  boardPath: string,
  itemId: string,
  reservationId?: string
): string {
  const base = `auditLogs/${boardPath}/${itemId}`;
  return reservationId ? `${base}/${reservationId}` : base;
}

// =============================================================================
// BOL COLLECTIONS (Firestore)
// =============================================================================
// Bill of Lading data is stored under parts/bol/{locationId}/ with three subcollections:
// bol-history, bol-addresses, and bol-carriers.

/**
 * Get collection path for BOL history
 *
 * @param locationId - Store location ID (11, 12, 13, etc.)
 * @param useProduction - Whether to use production collection (defaults to isProduction)
 * @returns Full Firestore collection path
 *
 * @example
 * getBolHistoryCollectionPath('11') // => 'artifacts/stamp-board-8b2fa/prod/parts/bol/11/bol-history'
 */
export function getBolHistoryCollectionPath(
  locationId: string,
  useProduction: boolean = isProduction
): string {
  const environment = getEnvironment('bolHistory', useProduction);
  return `artifacts/${appId}/${environment}/parts/bol/${locationId}/bol-history`;
}

/**
 * Get collection path for BOL address book
 *
 * @param locationId - Store location ID (11, 12, 13, etc.)
 * @param useProduction - Whether to use production collection (defaults to isProduction)
 * @returns Full Firestore collection path
 *
 * @example
 * getBolAddressesCollectionPath('11') // => 'artifacts/stamp-board-8b2fa/prod/parts/bol/11/bol-addresses'
 */
export function getBolAddressesCollectionPath(
  locationId: string,
  useProduction: boolean = isProduction
): string {
  const environment = getEnvironment('bolAddresses', useProduction);
  return `artifacts/${appId}/${environment}/parts/bol/${locationId}/bol-addresses`;
}

/**
 * Get collection path for BOL carrier book
 *
 * @param locationId - Store location ID (11, 12, 13, etc.)
 * @param useProduction - Whether to use production collection (defaults to isProduction)
 * @returns Full Firestore collection path
 *
 * @example
 * getBolCarriersCollectionPath('11') // => 'artifacts/stamp-board-8b2fa/prod/parts/bol/11/bol-carriers'
 */
export function getBolCarriersCollectionPath(
  locationId: string,
  useProduction: boolean = isProduction
): string {
  const environment = getEnvironment('bolCarriers', useProduction);
  return `artifacts/${appId}/${environment}/parts/bol/${locationId}/bol-carriers`;
}

// =============================================================================
// LOST REVENUE BOARD (Firestore)
// =============================================================================
// Cross-location board for tracking missed rental equipment requests.
// No location ID — shared across all locations.

/**
 * Get collection path for lost revenue items
 *
 * When divisionId is provided, returns a division-scoped subcollection path.
 * When divisionId is omitted, returns the legacy global path.
 *
 * @param divisionId - Optional division ID for division-scoped data
 * @param useProduction - Whether to use production collection (defaults to isProduction)
 * @returns Full Firestore collection path, or null if divisionId is null
 *
 * @example
 * getLostRevenueCollectionPath() // => 'artifacts/stamp-board-8b2fa/prod/rental/lost-revenue-items'
 * getLostRevenueCollectionPath('default') // => 'artifacts/stamp-board-8b2fa/prod/rental/lost-revenue-items/default/items'
 */
export function getLostRevenueCollectionPath(
  divisionId?: string | null,
  useProduction: boolean = isProduction
): string | null {
  const environment = useProduction ? 'prod' : 'dev';
  if (divisionId) {
    return `artifacts/${appId}/${environment}/rental/lost-revenue-items/${divisionId}/items`;
  }
  // Legacy global path (backward compatibility)
  return `artifacts/${appId}/${environment}/rental/lost-revenue-items`;
}

/**
 * Get collection path for lost revenue equipment rates
 *
 * When divisionId is provided, returns a division-scoped subcollection path.
 * When divisionId is omitted, returns the legacy global path.
 *
 * @param divisionId - Optional division ID for division-scoped data
 * @param useProduction - Whether to use production collection (defaults to isProduction)
 * @returns Full Firestore collection path, or null if divisionId is null
 *
 * @example
 * getLostRevenueRatesCollectionPath() // => 'artifacts/stamp-board-8b2fa/prod/rental/lost-revenue-rates'
 * getLostRevenueRatesCollectionPath('default') // => 'artifacts/stamp-board-8b2fa/prod/rental/lost-revenue-rates/default/items'
 */
export function getLostRevenueRatesCollectionPath(
  divisionId?: string | null,
  useProduction: boolean = isProduction
): string | null {
  const environment = useProduction ? 'prod' : 'dev';
  if (divisionId) {
    return `artifacts/${appId}/${environment}/rental/lost-revenue-rates/${divisionId}/items`;
  }
  // Legacy global path (backward compatibility)
  return `artifacts/${appId}/${environment}/rental/lost-revenue-rates`;
}

// =============================================================================
// GUIDES COLLECTION (Firestore)
// =============================================================================
// Per-page user guides stored globally (not location-specific).
// Document ID = pageId from pages.config.ts.

/**
 * Get collection path for page guides
 *
 * Guides are global (not location-specific), stored under the shared category.
 * Follows the same pattern as getDirectoryCollectionPath().
 *
 * @param useProduction - Whether to use production collection (defaults to isProduction)
 * @returns Full Firestore collection path
 *
 * @example
 * getGuidesCollectionPath() // => 'artifacts/stamp-board-8b2fa/prod/shared/guides'
 */
export function getGuidesCollectionPath(
  useProduction: boolean = isProduction
): string {
  const environment = useProduction ? 'prod' : 'dev';
  return `artifacts/${appId}/${environment}/shared/guides`;
}

// =============================================================================
// QUIZZES COLLECTIONS (Firestore)
// =============================================================================
// Env-namespaced global collections (same shape as guides). See docs/QUIZZES.md.
// Doc ids: quizKeys/{quizId}, quizAssignments/{quizId}_{email},
// quizResponses/{assignmentId}_{attempt}, quizGrades/{responseId}
// (id builders live in src/quizzes-react/utils/ids.ts).

/**
 * Quiz definitions (editors write; assignees read published/closed).
 *
 * @example
 * getQuizzesCollectionPath() // => 'artifacts/stamp-board-8b2fa/prod/shared/quizzes'
 */
export function getQuizzesCollectionPath(
  useProduction: boolean = isProduction
): string {
  const environment = useProduction ? 'prod' : 'dev';
  return `artifacts/${appId}/${environment}/shared/quizzes`;
}

/**
 * Answer keys, one doc per quiz (doc id = quizId). Never readable by assignees.
 *
 * @example
 * getQuizKeysCollectionPath() // => 'artifacts/stamp-board-8b2fa/prod/shared/quizKeys'
 */
export function getQuizKeysCollectionPath(
  useProduction: boolean = isProduction
): string {
  const environment = useProduction ? 'prod' : 'dev';
  return `artifacts/${appId}/${environment}/shared/quizKeys`;
}

/**
 * Quiz assignments, doc id = `{quizId}_{email}`.
 *
 * @example
 * getQuizAssignmentsCollectionPath() // => 'artifacts/stamp-board-8b2fa/prod/shared/quizAssignments'
 */
export function getQuizAssignmentsCollectionPath(
  useProduction: boolean = isProduction
): string {
  const environment = useProduction ? 'prod' : 'dev';
  return `artifacts/${appId}/${environment}/shared/quizAssignments`;
}

/**
 * Quiz attempts, doc id = `{assignmentId}_{attempt}`.
 *
 * @example
 * getQuizResponsesCollectionPath() // => 'artifacts/stamp-board-8b2fa/prod/shared/quizResponses'
 */
export function getQuizResponsesCollectionPath(
  useProduction: boolean = isProduction
): string {
  const environment = useProduction ? 'prod' : 'dev';
  return `artifacts/${appId}/${environment}/shared/quizResponses`;
}

/**
 * Editor-only authoritative grades, doc id = responseId.
 *
 * @example
 * getQuizGradesCollectionPath() // => 'artifacts/stamp-board-8b2fa/prod/shared/quizGrades'
 */
export function getQuizGradesCollectionPath(
  useProduction: boolean = isProduction
): string {
  const environment = useProduction ? 'prod' : 'dev';
  return `artifacts/${appId}/${environment}/shared/quizGrades`;
}

// =============================================================================
// QUIZ SCENARIO COLLECTIONS (Firestore)
// =============================================================================
// Runs of scenario templates. Global + env-namespaced like the quiz collections; the
// store is the `locationId` field. Clients only read these (every write goes through
// Flask). Mirrors webapp/config/collections.py. See docs/archive/QUIZ_SCENARIOS_PLAN.md.

/** Scenario runs (random doc id). */
export function getQuizScenariosCollectionPath(
  useProduction: boolean = isProduction
): string {
  const environment = useProduction ? 'prod' : 'dev';
  return `artifacts/${appId}/${environment}/shared/quizScenarios`;
}

/** Scenario steps, doc id `{scenarioId}_{index}`. */
export function getQuizScenarioStepsCollectionPath(
  useProduction: boolean = isProduction
): string {
  const environment = useProduction ? 'prod' : 'dev';
  return `artifacts/${appId}/${environment}/shared/quizScenarioSteps`;
}

/** One person's "step reassigned away from you" notices, doc id = step id. */
export function getQuizScenarioReassignmentsCollectionPath(
  email: string,
  useProduction: boolean = isProduction
): string {
  const environment = useProduction ? 'prod' : 'dev';
  return `artifacts/${appId}/${environment}/shared/quizScenarioReassignments/${email.trim().toLowerCase()}/items`;
}

// =============================================================================
// REFERENCES COLLECTIONS (Firestore)
// =============================================================================
// Reference documents are stored in Firestore with categories as subcollections.
// This allows flexible category management and document organization per page.

/**
 * Reference page identifiers
 * These map to permission page IDs and collection paths
 */
export type ReferencePageId = 'bobcatBook' | 'suppliesBook' | 'rentalReferences';

/**
 * Get collection path for reference categories
 *
 * Categories are stored per-page to allow different organization for each reference page.
 * When divisionId is provided, returns a division-scoped path.
 *
 * @param pageId - Reference page identifier
 * @param divisionId - Optional division ID for division-scoped data
 * @param useProduction - Whether to use production collection (defaults to isProduction)
 * @returns Full Firestore collection path, or null if divisionId is null
 *
 * NOTE: Division-scoped paths use an intermediate `divisions` collection to maintain
 * Firestore's odd-segment requirement for collection paths (7 → 9 segments).
 *
 * @example
 * getReferenceCategoriesPath('bobcatBook') // => 'artifacts/stamp-board-8b2fa/prod/shared/references/bobcatBook/categories'
 * getReferenceCategoriesPath('bobcatBook', 'abc123') // => 'artifacts/stamp-board-8b2fa/prod/shared/references/bobcatBook/divisions/abc123/categories'
 */
export function getReferenceCategoriesPath(
  pageId: ReferencePageId,
  divisionId?: string | null,
  useProduction: boolean = isProduction
): string | null {
  const environment = useProduction ? 'prod' : 'dev';
  if (divisionId) {
    return `artifacts/${appId}/${environment}/shared/references/${pageId}/divisions/${divisionId}/categories`;
  }
  // Legacy global path (backward compatibility)
  return `artifacts/${appId}/${environment}/shared/references/${pageId}/categories`;
}

/**
 * Get collection path for reference documents
 *
 * Documents are stored per-page with category assignments.
 * When divisionId is provided, returns a division-scoped path.
 *
 * NOTE: Division-scoped paths use an intermediate `divisions` collection to maintain
 * Firestore's odd-segment requirement for collection paths (7 → 9 segments).
 *
 * @param pageId - Reference page identifier
 * @param divisionId - Optional division ID for division-scoped data
 * @param useProduction - Whether to use production collection (defaults to isProduction)
 * @returns Full Firestore collection path, or null if divisionId is null
 *
 * @example
 * getReferenceDocumentsPath('bobcatBook') // => 'artifacts/stamp-board-8b2fa/prod/shared/references/bobcatBook/documents'
 * getReferenceDocumentsPath('bobcatBook', 'abc123') // => 'artifacts/stamp-board-8b2fa/prod/shared/references/bobcatBook/divisions/abc123/documents'
 */
export function getReferenceDocumentsPath(
  pageId: ReferencePageId,
  divisionId?: string | null,
  useProduction: boolean = isProduction
): string | null {
  const environment = useProduction ? 'prod' : 'dev';
  if (divisionId) {
    return `artifacts/${appId}/${environment}/shared/references/${pageId}/divisions/${divisionId}/documents`;
  }
  // Legacy global path (backward compatibility)
  return `artifacts/${appId}/${environment}/shared/references/${pageId}/documents`;
}

// =============================================================================
// LEGACY HELPERS
// =============================================================================

